import type { Feature } from '@cocoonspace/sdk-js/types/feature'
import { cocoonFeatures } from '@cocoonspace/shared/config/cocoon-features-config'
import { ChevronDown, ConciergeBell, X } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import {
	DrawerPopover,
	DrawerPopoverContent,
	DrawerPopoverTrigger,
} from '~/components/drawer-dialog'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { cn } from '~/lib/utils'

const getDefaultFeatureState = (value?: string[]) =>
	cocoonFeatures
		.filter((feat) => feat.isSearchFilter)
		.reduce(
			(acc, feat) => {
				acc[feat.id] = value?.includes(feat.id) || false

				return acc
			},
			{} as Record<string, boolean>,
		)

export const EquipmentsFilter = ({
	features,
	onEquipmentClick,
}: {
	features: {}
	onEquipmentClick: (featId: string, isChecked: boolean) => void
}) => {
	const { t } = useTranslation()

	return (
		<>
			<div className='mb-4'>{t('search:featuresFilter.title')}</div>

			<div className='mb-4 grid grid-cols-2 gap-2 md:max-w-[400px] md:grid-cols-3'>
				{Object.entries(features).map(([featId, isChecked]) => (
					<Card
						key={featId}
						data-testid={`feature-${featId}`}
						className={cn(
							'flex cursor-pointer flex-col items-center border px-4 py-1 text-center text-xs hover:border-primary',
							isChecked ? '!border-primary' : 'border-transparent',
						)}
						onClick={() => onEquipmentClick(featId, isChecked as boolean)}
					>
						<Image
							src={
								isChecked
									? `/images/v2/features/${featId}-on.svg`
									: `/images/v2/features/${featId}-off.svg`
							}
							alt={featId}
							width='89'
							height='71'
						/>

						{t(`features:${featId}`)}
					</Card>
				))}
			</div>
		</>
	)
}

export const SearchFeaturesFilter = ({
	onChange,
	value,
}: {
	onChange: (features?: string[]) => void
	value: string[] | undefined
}) => {
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)

	const [features, setFeatures] = useState<Record<Feature, boolean>>(() =>
		getDefaultFeatureState(value),
	)

	useEffect(() => {
		setFeatures(getDefaultFeatureState(value))
	}, [isOpen, value])

	const handleConfirm = () => {
		const checkedFeatures = Object.entries(features)
			.filter(([, isChecked]) => isChecked)
			.map(([featId]) => featId as Feature)

		onChange(checkedFeatures)
	}

	return (
		<DrawerPopover
			open={isOpen}
			onOpenChange={setIsOpen}
		>
			<Card className='flex h-[32px] flex-row items-center rounded text-sm'>
				<DrawerPopoverTrigger
					className='flex flex-1 flex-row items-center gap-2 p-2'
					data-testid='open-features-popup-btn'
				>
					<ConciergeBell
						size={14}
						className='text-primary'
					/>

					{t('search:featuresFilter.servicesCount', {
						count: value?.length ?? 0,
					})}

					{!value?.length && (
						<ChevronDown
							size={16}
							strokeWidth={1}
							className='shrink-0'
						/>
					)}
				</DrawerPopoverTrigger>

				{!!value?.length && (
					<button
						type='button'
						className='pr-2'
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							onChange(undefined)
							setIsOpen(false)
						}}
					>
						<X
							className='text-primary'
							size={16}
						/>
					</button>
				)}
			</Card>

			<DrawerPopoverContent
				sideOffset={10}
				className='flex max-h-[80vh] flex-col'
			>
				<div className='flex-1 overflow-y-auto'>
					<EquipmentsFilter
						features={features}
						onEquipmentClick={(featId, isChecked) => {
							setFeatures((features) => ({
								...features,
								[featId]: !isChecked,
							}))
						}}
					/>
				</div>

				<div className='flex flex-row justify-end gap-6'>
					<Button
						variant='link'
						size='sm'
						data-testid='reset-features-btn'
						onClick={() => {
							onChange(undefined)
							setIsOpen(false)
						}}
					>
						{t('search:featuresFilter.reset')}
					</Button>

					<Button
						data-testid='validate-features-btn'
						size='sm'
						variant='outline'
						onClick={() => {
							handleConfirm()
							setIsOpen(false)
						}}
					>
						{t('search:featuresFilter.validate')}
					</Button>
				</div>
			</DrawerPopoverContent>
		</DrawerPopover>
	)
}
